import { useState } from 'react';
import { api } from './api';
import { formatMoney, netPnl } from './pnl';
import { TAG_KINDS, TagChip, tagKind } from './tags';
import { useI18n } from './i18n';

// Lets the trader remove tags from their library. New tags are added from a trade's edit window.
export function TagManager({ library, onLibraryChange, onSessionExpired, inTradeWindow = false }) {
  const { t } = useI18n();
  const [error, setError] = useState('');

  async function removeTag(name) {
    setError('');
    try {
      const saved = await api('/tags', { method: 'PUT', body: library.filter((x) => x.name !== name) });
      onLibraryChange(saved);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    }
  }

  return (
    <div className="tag-manage">
      <p className="hint">
        {t("Remove tags you don't use. Trades keep their tags; removed ones just show as context.")}{' '}
        {inTradeWindow ? t('To add a new tag, click Done and use the box under your tags.') : t("Add new tags from any trade's edit window.")}
      </p>
      {TAG_KINDS.map(({ kind, label }) => (
        <div key={kind} className="tag-group">
          <span className="tag-group-label">{t(label)}</span>
          <div className="tag-list">
            {library.filter((x) => x.kind === kind).map((x) => (
              <TagChip key={x.name} name={x.name} kind={kind} onRemove={() => removeTag(x.name)} />
            ))}
          </div>
        </div>
      ))}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function TagReport({ trades, library, onLibraryChange, onSessionExpired, className = '' }) {
  const { t, tr } = useI18n();
  const [order, setOrder] = useState('worst');
  const [managing, setManaging] = useState(false);

  // Per-tag totals. A trade with several tags counts toward each of them.
  const stats = {};
  let untagged = { trades: 0, wins: 0, net: 0 };
  for (const t of trades) {
    const net = netPnl(t);
    const tags = t.tags && t.tags.length ? t.tags : null;
    if (!tags) {
      untagged = { trades: untagged.trades + 1, wins: untagged.wins + (net > 0 ? 1 : 0), net: untagged.net + net };
      continue;
    }
    for (const name of tags) {
      const key = name.toLowerCase();
      if (!stats[key]) stats[key] = { name, trades: 0, wins: 0, net: 0 };
      stats[key].trades += 1;
      stats[key].net += net;
      if (net > 0) stats[key].wins += 1;
    }
  }
  const rows = Object.values(stats).sort((a, b) => (order === 'worst' ? a.net - b.net : b.net - a.net));
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.net)));
  const worstMistake = Object.values(stats)
    .filter((r) => tagKind(r.name, library) === 'mistake' && r.net < 0)
    .sort((a, b) => a.net - b.net)[0];

  return (
    <section className={`tag-report ${className}`} id="tags">
      <div className="admin-panel-header">
        <div>
          <h2>{t('Tags')}</h2>
          <p className="admin-panel-sub">{t('Which setups make you money, and which mistakes cost you.')}</p>
        </div>
        <div className="inbox-filter">
          <button type="button" className={`secondary small${order === 'worst' ? ' active' : ''}`} onClick={() => setOrder('worst')}>
            {t('Worst first')}
          </button>
          <button type="button" className={`secondary small${order === 'best' ? ' active' : ''}`} onClick={() => setOrder('best')}>
            {t('Best first')}
          </button>
          <button type="button" className={`secondary small${managing ? ' active' : ''}`} onClick={() => setManaging(!managing)}>
            {t('Manage tags')}
          </button>
        </div>
      </div>

      {managing && <TagManager library={library} onLibraryChange={onLibraryChange} onSessionExpired={onSessionExpired} />}

      {worstMistake && (
        <p className="tag-callout">
          {tr(worstMistake.trades === 1 ? '<0>{name}</0> has cost you <1>{amount}</1> over {n} trade.' : '<0>{name}</0> has cost you <1>{amount}</1> over {n} trades.',
            [(x) => <strong>{x}</strong>, (x) => <span className="loss">{x}</span>],
            { name: worstMistake.name, amount: formatMoney(worstMistake.net), n: worstMistake.trades })}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="empty">
          {t('No tagged trades in this period. Click a day in the calendar on your Journal, then a trade, to tag it with a setup or mistake.')}
        </p>
      ) : (
        <div className="table-wrap">
          <table className="tag-table">
            <thead>
              <tr>
                <th>{t('Tag')}</th>
                <th>{t('Trades')}</th>
                <th>{t('Win rate')}</th>
                <th>{t('Net P&L')}</th>
                <th>{t('Avg per trade')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = (Math.abs(r.net) / maxAbs) * 50;
                return (
                  <tr key={r.name}>
                    <td><TagChip name={r.name} kind={tagKind(r.name, library)} /></td>
                    <td>{r.trades}</td>
                    <td>{Math.round((r.wins / r.trades) * 100)}%</td>
                    <td>
                      <div className="pnl-bar-cell">
                        <span className={r.net >= 0 ? 'gain' : 'loss'}>{formatMoney(r.net)}</span>
                        <span className="pnl-bar" aria-hidden="true">
                          <span
                            className={`pnl-bar-fill ${r.net >= 0 ? 'positive' : 'negative'}`}
                            style={r.net >= 0 ? { left: '50%', width: `${pct}%` } : { right: '50%', width: `${pct}%` }}
                          />
                        </span>
                      </div>
                    </td>
                    <td className={r.net >= 0 ? 'gain' : 'loss'}>{formatMoney(r.net / r.trades)}</td>
                  </tr>
                );
              })}
              {untagged.trades > 0 && (
                <tr className="untagged-row">
                  <td>{t('Untagged')}</td>
                  <td>{untagged.trades}</td>
                  <td>{Math.round((untagged.wins / untagged.trades) * 100)}%</td>
                  <td className={untagged.net >= 0 ? 'gain' : 'loss'}>{formatMoney(untagged.net)}</td>
                  <td className={untagged.net >= 0 ? 'gain' : 'loss'}>{formatMoney(untagged.net / untagged.trades)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default TagReport;
